import { useEffect, useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, useMap, useMapEvents } from 'react-leaflet'
import { Icon } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import markerIcon from '../../../img/online_marker.png'
import { StatusImageModal, StatusImageThumb } from './statusImage'

// Reuse the existing marker asset (avoids the leaflet default-icon webpack path issue,
// same approach as dashboard/VehiclesMarkerGen.jsx).
const unitIcon = new Icon({ iconUrl: markerIcon, iconSize: [22, 35], iconAnchor: [11, 35], popupAnchor: [0, -30] })
// Saturated, high-contrast palette (one color per unit). Paired with a white halo
// below so dots stand out against map tiles and generic blue POI markers.
const COLORS = ['#00b8d4', '#ff3d00', '#00c853', '#aa00ff', '#ffab00', '#d500f9']

// Fit the view to ALL dots for the selected range — covers the full coverage area
// (fixes the export map zoomed too tight). animate:false so html2canvas captures a
// settled map (no in-flight pan/zoom).
const FitAll = ({ entries }) => {
  const map = useMap()
  useEffect(() => {
    const lls = entries.flatMap(([, pts]) => pts.map(p => [p[0], p[1]]))
    if (lls.length) map.fitBounds(lls, { padding: [28, 28], maxZoom: 16, animate: false })
  }, [map, entries])
  return null
}

// Zoom-adaptive thinning: collapse the unit's points to ~one dot per CELL_PX screen cell
// at the CURRENT zoom, so density scales with the map (sparse when zoomed out for a
// coverage overview, denser as you zoom in — down to the data's 55 m floor). Only points
// in the padded viewport are considered. Per cell, prefer a point with a thumbnail key;
// otherwise keep the most recent (pts arrive oldest→newest, so a later point wins).
const CELL_PX = 30
const thinByPixels = (pts, map) => {
  const bounds = map.getBounds().pad(0.25)
  const cells = new Map()
  for (const p of pts) {
    if (!bounds.contains([p[0], p[1]])) continue
    const cp = map.latLngToContainerPoint([p[0], p[1]])
    const cell = `${Math.round(cp.x / CELL_PX)}:${Math.round(cp.y / CELL_PX)}`
    const existing = cells.get(cell)
    if (!existing || (p[2] && !existing[2]) || !existing[2]) cells.set(cell, p)
  }
  return Array.from(cells.values())
}

// Renders one unit's breadcrumb dots, re-thinned on every zoom/pan so the on-screen
// density stays roughly uniform regardless of zoom level.
const UnitDots = ({ pts, color, token, onEnlarge }) => {
  const map = useMap()
  const [tick, setTick] = useState(0)
  useMapEvents({ zoomend: () => setTick(t => t + 1), moveend: () => setTick(t => t + 1) })
  const shown = useMemo(() => thinByPixels(pts, map), [pts, map, tick]) // eslint-disable-line react-hooks/exhaustive-deps
  return shown.map((p, j) => (
    <CircleMarker key={`${p[0]},${p[1]},${j}`} center={[p[0], p[1]]} radius={5}
      pathOptions={{ color: '#ffffff', weight: 1.5, fillColor: color, fillOpacity: 0.9 }}>
      {p[2] && (
        <Popup>
          <StatusImageThumb s3Key={p[2]} token={token} onClick={() => onEnlarge(p[2])} />
          <div className="tm-img-cap">click to enlarge</div>
        </Popup>
      )}
    </CircleMarker>
  ))
}

// tracks: { serial: [[lat,lon,key|null], ...] } — downsampled GPS positions per unit.
// Rendered as breadcrumb dots (not a connecting line): the samples are minutes apart,
// so a polyline between them would imply travel that didn't happen. Dots honestly show
// where each unit has been over the range; the labelled pin marks its latest position.
export const UnitMap = ({ tracks }) => {
  const token = useSelector(s => s.app.auth.token)
  const [enlarged, setEnlarged] = useState(null)
  const entries = Object.entries(tracks || {}).filter(([, pts]) => pts && pts.length)
  if (!entries.length) return <p className="tm-empty">No GPS positions for the selected units/range.</p>
  const firstPts = entries[0][1]
  const center = firstPts[firstPts.length - 1]
  return (
    <>
    <MapContainer center={[center[0], center[1]]} zoom={12} scrollWheelZoom preferCanvas className="tm-map">
      <TileLayer
        crossOrigin="anonymous"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {entries.map(([serial, pts], i) => {
        const last = pts[pts.length - 1]
        const color = COLORS[i % COLORS.length]
        return (
          <div key={serial}>
            <UnitDots pts={pts} color={color} token={token} onEnlarge={setEnlarged} />
            <Marker position={[last[0], last[1]]} icon={unitIcon}>
              <Popup><b>{serial}</b><br />{pts.length} sampled positions<br />latest: {last[0].toFixed(5)}, {last[1].toFixed(5)}</Popup>
            </Marker>
          </div>
        )
      })}
      <FitAll entries={entries} />
    </MapContainer>
    {enlarged && <StatusImageModal s3Key={enlarged} token={token} onClose={() => setEnlarged(null)} />}
    </>
  )
}
